import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUp, ChevronDown, Maximize2, MessageSquare, Minimize2, Pin, PinOff, Plus, X } from 'lucide-react';
import { AskProvider, useAsk } from './AskProvider';
import { Timeline } from './AskTimeline';
import { chipText, dockHiddenOn, inAppShell } from '../../lib/askContext';
import { useFeatureStrict } from '../../lib/features';
import { rememberPostLoginRedirect, useStore } from '../../store/StoreContext';
import { fetchAiKeyStatus } from '../../api/reportApi';

// The one persistent Ask Zmist composer. Mounted once at app level (App.tsx),
// so the conversation, its history and the running job survive every
// navigation. It shows what it is looking at (the context chip), can be
// expanded to the whole history from any screen, and never takes the screen
// away from the user while they work.

export function AskRoot() {
  const enabled = useFeatureStrict('ask.builder');
  const { isLoggedIn } = useStore();
  if (!enabled) return null;
  if (!isLoggedIn) return <AskGuestBar />;
  return (
    <AskProvider>
      <AskDock />
    </AskProvider>
  );
}

const TONE: Record<string, string> = {
  idle: 'text-black/50 dark:text-white/50',
  working: 'text-blue-600 dark:text-blue-400',
  ready: 'text-blue-600 dark:text-blue-400',
  attention: 'text-amber-700 dark:text-amber-400',
  error: 'text-red-600 dark:text-red-400',
};

const NEW_CONVERSATION = '__new__';

// Signed-out visitors can read public channels but not use AI: this is the
// only Ask surface they get. It sends them to the existing sign-in flow and
// brings them back to the same page (the dock derives its context from the
// URL, so the channel survives the round trip).
function AskGuestBar() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  if (!pathname.startsWith('/public')) return null;
  const signIn = () => {
    rememberPostLoginRedirect(pathname + search);
    navigate('/login');
  };
  return (
    <div className="fixed z-40 inset-x-0 bottom-0 px-3 pb-3 pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-3xl flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-black/10 dark:border-white/12 bg-[var(--color-surface)] px-3 py-2">
        <span className="text-[12.5px] font-medium shrink-0">Ask Zmist</span>
        <span className="text-[13px] text-black/60 dark:text-white/60 flex-1 min-w-[12rem]">Sign in and connect your AI provider API key to get started.</span>
        <button type="button" onClick={signIn} className="h-9 px-3 rounded-lg text-[13px] font-medium bg-blue-600 text-white hover:bg-blue-700">
          Sign in to ask
        </button>
      </div>
    </div>
  );
}

export function AskDock() {
  const ask = useAsk();
  const { pathname } = useLocation();
  const [text, setText] = useState('');
  const [chipOpen, setChipOpen] = useState(false);
  // Shell-style recall: ArrowUp walks back through the messages sent from here,
  // ArrowDown forward again to the unsent draft. -1 = not recalling.
  const recall = useRef({ at: -1, draft: '' });
  const inputRef = useRef<HTMLInputElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true); // follow new content only while the reader is at the bottom
  const [away, setAway] = useState(false);
  const hidden = dockHiddenOn(pathname);
  const shell = inAppShell(pathname);
  const { context, status } = ask;
  const { channels, isChannelOwner, aiSettings } = useStore();
  // A key saved on the account is never sent back to the browser, so ask the
  // backend which providers have one as well as checking this browser's copy.
  const [savedKey, setSavedKey] = useState(false);
  useEffect(() => {
    fetchAiKeyStatus()
      .then((list) => setSavedKey(list.some((s) => s.saved)))
      .catch(() => {});
  }, []);
  const hasKey = savedKey || Object.values(aiSettings?.apiKeys ?? {}).some((k) => (k ?? '').trim() !== '');
  const keyHref = `/settings/ai?from=${encodeURIComponent(pathname)}`;
  const mine = channels.filter((c) => isChannelOwner(c) && c.nickname).slice(0, 60);

  // Reserve room so the dock never covers a screen's own bottom controls
  // (ChatScreen's compose bar subtracts --ask-dock-h from its height). Only on
  // desktop in the app shell; on phones the dock is a floating button.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const apply = () => document.documentElement.style.setProperty('--ask-dock-h', !hidden && shell && mq.matches ? '3.5rem' : '0px');
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.documentElement.style.removeProperty('--ask-dock-h');
    };
  }, [hidden, shell]);

  // Esc closes the chip popover first, then the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (chipOpen) setChipOpen(false);
      else if (ask.open) ask.setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chipOpen, ask]);

  // Keep the newest step in view while a run streams in; never yank the
  // reader who has scrolled up to read.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && ask.open && stick.current) el.scrollTop = el.scrollHeight;
  }, [ask.items, ask.open, ask.full, ask.error]);
  useEffect(() => {
    if (ask.open) stick.current = true;
  }, [ask.open]);
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stick.current = near;
    setAway(!near);
  };
  const toLatest = () => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  if (hidden) return null;

  const onRecall = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    const sent = ask.items.flatMap((i) => (i.type === 'message' && i.event.role === 'user' && i.event.text ? [i.event.text] : []));
    const r = recall.current;
    if (e.key === 'ArrowUp') {
      if (sent.length === 0) return;
      e.preventDefault();
      if (r.at < 0) r.draft = text;
      r.at = r.at < 0 ? sent.length - 1 : Math.max(0, r.at - 1);
      setText(sent[r.at]);
    } else if (r.at >= 0) {
      e.preventDefault();
      r.at += 1;
      if (r.at >= sent.length) {
        r.at = -1;
        setText(r.draft);
      } else setText(sent[r.at]);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const t = text;
    if (!t.trim()) return;
    recall.current.at = -1;
    setText('');
    if (!(await ask.send(t))) setText(t);
  };

  const pinnedMismatch = !!ask.pinned && !!context.channel && ask.pinned !== context.channel;
  const placeholder = ask.items.length === 0 ? 'Paste a data link, or describe a channel' : context.channel ? 'Change this channel, or ask a question…' : 'Describe a channel, or ask…';

  const chip = (
    <div className="relative min-w-0">
      <button
        type="button"
        onClick={() => setChipOpen(!chipOpen)}
        aria-expanded={chipOpen}
        aria-haspopup="dialog"
        aria-label={`Context: ${chipText(context)}${ask.pinned ? `, pinned to ${ask.pinned}` : ''}. Show details`}
        className="inline-flex items-center gap-1.5 max-w-full md:max-w-[16rem] h-7 md:h-8 px-2 md:px-2.5 rounded-lg border border-black/10 dark:border-white/12 text-[12px] md:text-[12.5px] hover:bg-black/5 dark:hover:bg-white/10"
      >
        {ask.pinned && <Pin size={12} aria-hidden className="shrink-0 text-blue-600 dark:text-blue-400" />}
        <span className="truncate">{ask.pinned && !context.channel ? `Pinned: ${ask.pinned}` : chipText(context)}</span>
        <ChevronDown size={12} aria-hidden className="shrink-0 opacity-60" />
      </button>
      {chipOpen && (
        <div role="dialog" aria-label="Ask context" className="absolute bottom-full mb-2 left-0 z-50 w-[min(18rem,calc(100vw-1.5rem))] rounded-xl border border-black/10 dark:border-white/12 bg-[var(--color-surface)] p-3 text-[13px] shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
          <div role="listbox" aria-label="Channel history" className="mb-3 max-h-48 overflow-y-auto -mx-1">
            <button type="button" role="option" aria-selected={!ask.pinned} onClick={() => { ask.setPinned(''); setChipOpen(false); }} className={`w-full text-left h-8 px-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 ${!ask.pinned ? 'font-medium' : ''}`}>
              {context.channel ? 'This page' : 'My Channels · new chat'}
            </button>
            {mine.map((c) => (
              <button key={c.id} type="button" role="option" aria-selected={ask.pinned === c.nickname} onClick={() => { ask.setPinned(c.nickname); setChipOpen(false); }} className={`w-full text-left h-8 px-2 rounded-lg truncate hover:bg-black/5 dark:hover:bg-white/10 ${ask.pinned === c.nickname ? 'font-medium' : ''}`}>
                {c.title || c.nickname}
              </button>
            ))}
          </div>
          <dl className="m-0 grid grid-cols-[5.5rem_1fr] gap-y-1">
            <dt className="text-black/50 dark:text-white/50">Page</dt>
            <dd className="m-0 break-words">{context.area}{context.tabLabel ? ` · ${context.tabLabel}` : ''}</dd>
            {context.channel && (
              <>
                <dt className="text-black/50 dark:text-white/50">Channel</dt>
                <dd className="m-0 break-all">{context.channel}</dd>
                <dt className="text-black/50 dark:text-white/50">Visibility</dt>
                <dd className="m-0">{[context.visibility, context.readOnly ? 'Read-only' : 'You own it'].filter(Boolean).join(' · ') || 'Unknown'}</dd>
              </>
            )}
            <dt className="text-black/50 dark:text-white/50">Pinned</dt>
            <dd className="m-0">{ask.pinned || 'Nothing - follows the page'}</dd>
          </dl>
          {pinnedMismatch && <p className="m-0 mt-2 text-amber-700 dark:text-amber-400">This page and the pinned channel differ. Zmist will ask which one you mean before changing anything.</p>}
          <p className="m-0 mt-2 text-[12px] text-black/50 dark:text-white/50">Asking never widens access: Zmist re-checks what you own on the server.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {context.channel && ask.pinned !== context.channel && (
              <button type="button" onClick={() => ask.setPinned(context.channel!)} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-black/15 dark:border-white/20 hover:bg-black/5 dark:hover:bg-white/10">
                <Pin size={13} aria-hidden /> Pin this channel
              </button>
            )}
            {ask.pinned && (
              <button type="button" onClick={() => ask.setPinned('')} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-black/15 dark:border-white/20 hover:bg-black/5 dark:hover:bg-white/10">
                <PinOff size={13} aria-hidden /> Unpin
              </button>
            )}
            {(ask.pinned || context.channel) && (
              <Link to={`/channels/${encodeURIComponent(ask.pinned || context.channel!)}`} onClick={() => setChipOpen(false)} className="inline-flex items-center h-8 px-2.5 rounded-lg hover:underline text-blue-600 dark:text-blue-400">
                Go to channel
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const composer = (
    <form onSubmit={submit} className="flex items-center gap-2 min-w-0 flex-1">
      <label className="sr-only" htmlFor="ask-input">
        Ask Zmist
      </label>
      <input
        id="ask-input"
        ref={inputRef}
        value={text}
        onChange={(e) => {
          recall.current.at = -1;
          setText(e.target.value);
        }}
        onKeyDown={onRecall}
        placeholder={placeholder}
        maxLength={2000}
        autoComplete="off"
        className="flex-1 min-w-0 h-11 md:h-9 rounded-lg bg-black/[0.03] dark:bg-white/[0.04] border border-black/10 dark:border-white/12 px-3 text-[16px] md:text-[14px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
      />
      <button type="submit" disabled={ask.busy || !text.trim()} aria-label="Send" className="h-11 w-11 md:h-9 md:w-9 shrink-0 inline-flex items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
        <ArrowUp size={17} aria-hidden />
      </button>
    </form>
  );

  const pos = shell ? 'md:left-64 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] md:bottom-10' : 'bottom-0';

  return (
    <>
      {/* Live status for assistive tech: only the one-line summary, not every step. */}
      <div className="sr-only" role="status" aria-live="polite">
        {status.text}
      </div>

      {/* ---- expanded panel: the entire history ---- */}
      {ask.open && (
        <div
          role="dialog"
          aria-label="Ask Zmist conversation"
          className={`fixed z-40 inset-x-0 ${shell ? 'md:left-64' : ''} ${
            ask.full ? 'top-2 md:top-4 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] md:bottom-[5.5rem]' : 'bottom-[calc(3.5rem+env(safe-area-inset-bottom))] md:bottom-[5.5rem] top-auto max-h-[70vh]'
          } flex justify-center px-0 md:px-4 pointer-events-none`}
        >
          <div className={`relative pointer-events-auto w-full ${ask.full ? 'max-w-4xl' : 'max-w-3xl'} flex flex-col bg-[var(--color-surface)] border border-black/10 dark:border-white/12 md:rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.18)] overflow-hidden ${ask.full ? 'h-full' : 'max-h-[70vh]'}`}>
            <header className="border-b border-black/10 dark:border-white/10">
              <div className="flex items-center gap-2 px-3 py-2">
                <MessageSquare size={15} aria-hidden className="opacity-60 shrink-0" />
                <h2 className="m-0 text-[14px] font-medium truncate flex-1 min-w-0">{ask.conversation?.title || 'Ask Zmist'}</h2>
                <button type="button" onClick={ask.newConversation} aria-label="New conversation" className="h-9 w-9 md:h-8 md:w-8 shrink-0 inline-flex items-center justify-center rounded-lg hover:bg-black/5 dark:hover:bg-white/10">
                  <Plus size={16} aria-hidden />
                </button>
                <button type="button" onClick={() => ask.setFull(!ask.full)} aria-label={ask.full ? 'Shrink history' : 'Expand history'} className="hidden md:inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg hover:bg-black/5 dark:hover:bg-white/10">
                  {ask.full ? <Minimize2 size={15} aria-hidden /> : <Maximize2 size={15} aria-hidden />}
                </button>
                <button type="button" onClick={() => ask.setOpen(false)} aria-label="Close history" className="h-9 w-9 md:h-8 md:w-8 shrink-0 inline-flex items-center justify-center rounded-lg hover:bg-black/5 dark:hover:bg-white/10">
                  <X size={16} aria-hidden />
                </button>
              </div>
              {/* its own line: a full-width switcher, with "New conversation" in it */}
              <div className="px-3 pb-2">
                <select
                  aria-label="Switch conversation"
                  value={ask.conversation?.id ?? NEW_CONVERSATION}
                  onChange={(e) => (e.target.value === NEW_CONVERSATION ? ask.newConversation() : ask.switchConversation(e.target.value))}
                  className="h-9 md:h-8 w-full rounded-lg border border-black/10 dark:border-white/12 bg-transparent px-2 text-[13px] md:text-[12px]"
                >
                  <option value={NEW_CONVERSATION}>+ New conversation</option>
                  {ask.conversations.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>
            </header>
            <div ref={scroller} onScroll={onScroll} className="relative flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3">
              <Timeline items={ask.items} busy={ask.busy} runningExec={ask.active?.id} onDecide={ask.decide} onCancel={ask.cancel} onGo={(u) => { ask.go(u); }} onReply={(t) => { void ask.send(t); }} />
              {ask.error && <p role="alert" className="mt-3 text-[13px] text-red-600 dark:text-red-400">{ask.error}</p>}
            </div>
            {away && (
              <button type="button" onClick={toLatest} className="absolute bottom-16 md:bottom-4 left-1/2 -translate-x-1/2 h-8 px-3 rounded-full border border-black/10 dark:border-white/15 bg-[var(--color-surface)] text-[12.5px] shadow-[0_8px_20px_rgba(0,0,0,0.18)]">
                Jump to latest
              </button>
            )}
            {/* phones: the composer lives in the sheet */}
            <div className="md:hidden flex flex-col gap-2 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t border-black/10 dark:border-white/10">
              <div className="flex">{chip}</div>
              {composer}
            </div>
          </div>
        </div>
      )}

      {/* ---- finished while you were elsewhere ---- */}
      {ask.readyToOpen && !ask.open && (
        <div className={`fixed z-40 inset-x-3 ${shell ? 'md:left-72' : ''} bottom-[calc(7rem+env(safe-area-inset-bottom))] md:bottom-[6rem] flex justify-center pointer-events-none`}>
          <div className="pointer-events-auto flex items-center gap-3 rounded-xl border border-black/10 dark:border-white/12 bg-[var(--color-surface)] px-3 py-2 text-[13px] shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
            <span>Your channel is ready.</span>
            <button type="button" onClick={() => { ask.go(ask.readyToOpen!.url); ask.dismissReady(); }} className="h-8 px-3 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700">
              {ask.readyToOpen.label}
            </button>
            <button type="button" aria-label="Dismiss" onClick={ask.dismissReady} className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-black/5 dark:hover:bg-white/10">
              <X size={15} aria-hidden />
            </button>
          </div>
        </div>
      )}

      {/* ---- the dock ---- */}
      <div className={`fixed z-40 inset-x-0 ${pos} pointer-events-none`}>
        {/* Signed in but no AI key: say why one helps, without blocking
            anything (channels and factual answers work without it). */}
        {!hasKey && !ask.open && (
          <div className="hidden md:block px-4 pb-1">
            <p className="pointer-events-auto mx-auto max-w-3xl m-0 text-[12.5px] text-black/60 dark:text-white/60">
              Explanations and analysis use your own AI provider key.{' '}
              <Link to={keyHref} className="text-blue-600 dark:text-blue-400 hover:underline">Add your key</Link>, then come back here.
            </p>
          </div>
        )}
        {/* phones: a floating button; the composer is in the sheet */}
        {!ask.open && (
        <div className="md:hidden flex justify-end px-3 pb-2">
          <button type="button" onClick={() => ask.setOpen(!ask.open)} aria-label={`Ask Zmist${status.text ? `: ${status.text}` : ''}`} className="pointer-events-auto relative h-11 w-11 inline-flex items-center justify-center rounded-full bg-blue-600 text-white shadow-[0_12px_32px_rgba(0,0,0,0.25)]">
            <MessageSquare size={19} aria-hidden />
            {(status.tone === 'working' || status.tone === 'attention') && <span className={`absolute top-1 right-1 h-2.5 w-2.5 rounded-full border border-white ${status.tone === 'working' ? 'bg-white motion-safe:animate-pulse' : 'bg-amber-400'}`} />}
          </button>
        </div>
        )}
        <div className="hidden md:block px-4">
          <div className="pointer-events-auto mx-auto max-w-3xl flex items-center gap-2 h-12 rounded-xl border border-black/10 dark:border-white/12 bg-[var(--color-surface)] px-2">
            <span className="text-[12.5px] font-medium shrink-0 pl-1">Ask Zmist</span>
            {chip}
            {composer}
            <button type="button" onClick={() => ask.setOpen(!ask.open)} aria-expanded={ask.open} className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[12.5px] hover:bg-black/5 dark:hover:bg-white/10 shrink-0 max-w-[12rem] ${TONE[status.tone]}`}>
              <span className="truncate">{status.text || 'History'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
